import { createFileRoute } from "@tanstack/react-router";

import type { IntegrationStepConfig } from "../../../components/integrations/IntegrationPageShell";
import { ApiKeyPreviewField } from "../../../components/integrations/ApiKeyPreviewField";
import { CopyField } from "../../../components/integrations/CopyField";
import { IntegrationPageShell } from "../../../components/integrations/IntegrationPageShell";
import { useNubankCredentials } from "../../../components/integrations/useNubankCredentials";

export const Route = createFileRoute("/_authed/integrations/cursor")({
  component: CursorIntegrationPage,
  head: () => ({
    meta: [{ title: "Cursor | NBDC" }],
  }),
});

function CursorIntegrationPage() {
  const { keyPreview, baseUrl, customModelId, isPending, errorMessage } =
    useNubankCredentials();

  const openAiBaseUrl = `${baseUrl}/v1`;

  const steps: IntegrationStepConfig[] = [
    {
      title: "Open Cursor Model Config Page",
      description: "Navigate to the model configuration settings in Cursor",
      content: (
        <ol className="ml-5 list-decimal space-y-1 text-sm text-muted-foreground">
          <li>Open Cursor Settings</li>
          <li>Navigate to the Models section</li>
          <li>Click on OpenAI API Key settings</li>
        </ol>
      ),
    },
    {
      title: "Set the OpenAI Base URL and API Key",
      description: "Configure Cursor to use the custom API endpoint",
      content: (
        <div className="flex flex-col gap-4">
          <ApiKeyPreviewField
            value={keyPreview}
            isLoading={isPending}
            errorMessage={errorMessage}
          />
          <CopyField label="OpenAI Base URL" value={openAiBaseUrl} />
          <ol className="ml-5 list-decimal space-y-1 text-sm text-muted-foreground">
            <li>
              Paste your saved API key in the{" "}
              <span className="font-medium text-foreground">
                OpenAI API Key
              </span>{" "}
              field
            </li>
            <li>
              Paste the Base URL in{" "}
              <span className="font-medium text-foreground">
                Override OpenAI Base URL
              </span>
            </li>
            <li>Save the settings</li>
          </ol>
        </div>
      ),
    },
    {
      title: "Add the Custom Model",
      description: "Configure the custom model in Cursor",
      content: (
        <div className="flex flex-col gap-4">
          <CopyField
            label="Custom Model Name"
            value={customModelId}
            isLoading={isPending}
            errorMessage={errorMessage}
          />
          <ol className="ml-5 list-decimal space-y-1 text-sm text-muted-foreground">
            <li>In Cursor settings, find the model configuration section</li>
            <li>Add a new custom model entry</li>
            <li>Enter the model name shown above</li>
            <li>Save the configuration</li>
          </ol>
        </div>
      ),
    },
    {
      title: "Use the New Model",
      description: "Start using the custom model in Cursor",
      content: (
        <ol className="ml-5 list-decimal space-y-1 text-sm text-muted-foreground">
          <li>Open the Cursor chat interface</li>
          <li>Click the model selector dropdown</li>
          <li>Select the custom model from the list</li>
          <li>Start chatting with the model!</li>
        </ol>
      ),
    },
  ];

  return (
    // loomSrc="https://www.loom.com/embed/5933dd0cda0547389e6ecbcc381694ac"
    <IntegrationPageShell
      title="Cursor"
      description="Configure Cursor to use the custom Nubank model."
      steps={steps}
    />
  );
}
